<template>
  <label for="phone" class="input-phone">
    <div class="input-phone__head">
      <span class="input-phone__label">{{ title }}</span>
      <slot name="after-label" />
    </div>
    <input-mask
      ref="inputPhone"
      id="phone"
      aria-label="Введите ваш номер телефона"
      autocomplete="tel"
      class="input-phone__input"
      v-bind:class="{ empty: isEmpty }"
      v-bind:required="required"
      type="tel"
      v-bind:aria-invalid="hasViolations"
      v-bind:disabled="disabled"
      v-bind:lazy="isLazy"
      mask="{+7} (000) 000-0000"
      v-bind:pattern="$options.numberPhonePatternWithDelimiters"
      v-bind:placeholder="$options.numberPhonePlaceholder"
      v-bind:value="maskedInputValue"
      v-on:input="onInput"
      v-on:focus="onFocus"
      v-on:blur="onBlur"
    />
    <p
      class="input-phone__violation"
      role="alert"
      v-for="violationText in violations"
      v-bind:key="violationText"
    >{{ violationText }}</p>
  </label>
</template>
<script>
import { IMaskComponent } from 'vue-imask';

/**
 * @description Шаблон маски номера телефона с разделителями.
 * @type {string}
 */
const numberPhoneMaskWithDelimiters = '+7 (###) ###-####';

/**
 * @description Символы и выражения маски номера телефона с разделителями.
 * @type {(RegExp|string)[]}
 */
const numberPhoneMaskWithDelimitersChars = Array
  .from(numberPhoneMaskWithDelimiters)
  .map((char) => (char === '#' ? /[0-9]/ : char));

/**
 * @description Регулярные шаблон корректного номера телефона с разделителями.
 * @type {string}
 */
const numberPhonePatternWithDelimiters = '^\\+7 \\([0-9]{3}\\) [0-9]{3}-[0-9]{4}$';

/**
 * @description Заполнитель ввода номера телефона.
 * @type {string}
 */
const numberPhonePlaceholder = '+7 (___) ___-____';

/**
 * @description Компонент ввода номера телефона.
 * @type {Vue}
 */
export default {
  numberPhoneMaskWithDelimiters,
  components: {
    InputMask: IMaskComponent,
  },
  mounted() {
    this.$refs.inputPhone.$el.setAttribute('pattern', numberPhonePatternWithDelimiters);
  },
  computed: {
    /**
     * @description Имеет нарушения.
     * @type {boolean}
     */
    hasViolations() {
      return this.violations.length > 0;
    },
    isEmpty() {
      return this.value.replace(/[ _)(-]/g, '').length <= 2;
    },
    /**
     * @description Ввод корректен.
     * @type {boolean}
     */
    isValid() {
      return this.$options.numberPhonePatternRegEx.test(this.maskedInputValue);
    },
  },
  data() {
    return {
      maskedInputValue: this.normalizePrefixPhoneNumber(this.value),
      isLazy: true,
    };
  },
  methods: {
    /**
     * @description Заменить префикс номера телефона на стандартный.
     * @param {string} input Исходная строка.
     * @returns {string}
     */
    normalizePrefixPhoneNumber(input) {
      if (input.startsWith('8')) {
        return `+7${input.substring(1)}`;
      }
      if (input.startsWith('7')) {
        return `+${input}`;
      }
      return input;
    },
    onInput(value) {
      this.maskedInputValue = value;
      const withoutDelimitersValue = value.replace(/[ _)(-]/g, '');
      if (this.isValid) {
        this.$emit('input', withoutDelimitersValue);
      } else {
        this.$emit('input', '');
      }
      this.$emit('incomplete-input', withoutDelimitersValue);
    },
    onFocus() {
      if (this.maskedInputValue === '') {
        this.isLazy = false;
      }
    },
    onBlur() {
      if (this.isEmpty) {
        this.isLazy = true;
        this.onInput('');
      }
    },
  },
  name: 'InputPhone',
  numberPhoneMaskWithDelimitersChars,
  numberPhonePatternRegEx: new RegExp(numberPhonePatternWithDelimiters),
  numberPhonePatternWithDelimiters,
  numberPhonePlaceholder,
  props: {
    /**
     * @description Отключено.
     */
    disabled: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Заголовок.
     * @type {string}
     */
    title: { default: 'Телефон', type: String },
    /**
     * @description Значение.
     * @type {string}
     */
    value: {
      default: () => '',
      type: String,
    },
    /**
     * @description Список нарушений.
     * @type {string[]}
     */
    violations: {
      default: () => [],
      type: Array,
    },
    /**
     * @description Обязательное поле
     * @type {Boolean}
     */
    required: {
      default: () => true,
      type: Boolean,
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
@use "@/scss/fields";
.input-phone {
  display: flex;
  flex-direction: column;
  & &__head {
    display: flex;
    align-items: center;
    gap: 7px;
  }
  & &__label {
    color: var(--unfocus-color);
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    margin: 0 0 2px 0;
  }
  & &__input {
    @include fields.input-field();
    margin-bottom: 2px;
    &.empty:not(:focus) {
      color: var(--unfocus-color);
    }
  }
  & &__violation {
    color: var(--warning-color);
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;
    padding: .125rem;
  }
}
</style>
